<script setup>
import { computed, ref } from 'vue';
import Avatar from '@/components/ui/Avatar.vue';
import { Icon } from '@/components/ui/icon';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/shadcn/components/ui/dropdown-menu';
import ResendInvitationDialog from '@/components/Tour/ResendInvitationDialog.vue';
import BrandBarcodeBar from '@/components/Tour/BrandBarcodeBar.vue';
import TourExponent from '@/models/TourExponent.js';

const props = defineProps({
  exponent: {
    type: Object,
    required: true,
  },
  tourId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['detached']);

const detaching = ref(false);

const isValidated = computed(() => props.exponent.validated_at !== null);

const statusStyle = computed(() => ({
  ready: { chip: 'tw:bg-green-50 tw:text-green-700', dot: 'tw:bg-green-500' },
  partial: { chip: 'tw:bg-amber-50 tw:text-amber-700', dot: 'tw:bg-amber-500' },
}[props.exponent.status] ?? { chip: 'tw:bg-zinc-50 tw:text-zinc-600', dot: 'tw:bg-zinc-400' }));

const validatedAtLabel = computed(() => {
  if (!props.exponent.validated_at) {
    return '';
  }

  return new Date(props.exponent.validated_at).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
});

const qrOrder = computed(() => props.exponent.barcode_order);

const orderDateLabel = computed(() => {
  if (!qrOrder.value?.date) {
    return '';
  }

  return new Date(qrOrder.value.date).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
});

const brandIsReady = (brand) => brand.parked_equipments_count > 0 && brand.parks_selected_count > 0;

const detach = async () => {
  if (detaching.value) {
    return;
  }

  detaching.value = true;

  try {
    await new TourExponent({ id: props.exponent.id })
      .config({ params: { tour_id: props.tourId } })
      .delete();
    emit('detached', props.exponent.id);
  } catch (error) {
    console.error(error);
  } finally {
    detaching.value = false;
  }
};
</script>

<template>
  <div
    class="tw:rounded-lg tw:border tw:overflow-hidden"
    :class="isValidated
      ? 'tw:border-zinc-200 tw:bg-white'
      : 'tw:border-dashed tw:border-amber-300 tw:bg-amber-50/40'"
  >
    <!-- Header -->
    <div class="tw:flex tw:items-center tw:gap-3 tw:px-4 tw:py-3">
      <a :href="exponent.links.show" class="tw:block tw:shrink-0">
        <Avatar :size="40" :username="exponent.name" />
      </a>

      <div class="tw:min-w-0 tw:flex-1">
        <a :href="exponent.links.show" class="tw:font-semibold tw:text-zinc-900 tw:hover:underline">
          {{ exponent.name }}
        </a>
        <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-0.5 tw:mt-0.5 tw:text-xs">
          <span v-if="isValidated" class="tw:inline-flex tw:items-center tw:gap-1 tw:font-medium tw:text-green-600">
            <Icon name="ShieldCheck" class="tw:h-3.5 tw:w-3.5" />
            {{ $t('Validated') }} · {{ validatedAtLabel }}
          </span>
          <span v-else class="tw:inline-flex tw:items-center tw:gap-1 tw:font-medium tw:text-amber-600">
            <Icon name="CircleDashed" class="tw:h-3.5 tw:w-3.5" />
            {{ $t('Awaiting acceptance') }}
          </span>

          <a
            v-if="isValidated && qrOrder"
            :href="qrOrder.url"
            :title="$t('{count} barcodes assigned out of {requested} requested', { count: qrOrder.quantity_assigned, requested: qrOrder.quantity_requested })"
            class="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-full tw:border tw:border-blue-200 tw:bg-blue-50 tw:px-2 tw:py-0.5 tw:font-medium tw:text-blue-700 tw:no-underline tw:hover:underline"
          >
            <Icon name="ScanQrCode" class="tw:h-3.5 tw:w-3.5" />
            <span>#{{ qrOrder.id }}</span>
            <span class="tw:tabular-nums">· {{ $t('barcodes_count', { count: qrOrder.quantity_requested }, qrOrder.quantity_requested) }}</span>
            <span v-if="orderDateLabel">· {{ orderDateLabel }}</span>
          </a>
          <span
            v-else-if="isValidated"
            class="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-full tw:border tw:border-red-200 tw:bg-red-50 tw:px-2 tw:py-0.5 tw:font-medium tw:text-red-600"
          >
            <Icon name="ScanQrCode" class="tw:h-3.5 tw:w-3.5" />
            {{ $t('No barcode order') }}
          </span>
        </div>
      </div>

      <span
        v-if="isValidated"
        class="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-full tw:px-2.5 tw:py-1 tw:text-xs tw:font-semibold tw:whitespace-nowrap"
        :class="statusStyle.chip"
      >
        <span class="tw:h-1.5 tw:w-1.5 tw:rounded-full" :class="statusStyle.dot"></span>
        {{ exponent.status_label }}
      </span>

      <!-- Actions -->
      <ResendInvitationDialog
        :resend-url="exponent.links.resend"
        :permissions="exponent.permissions"
      >
        <template #trigger="{ open }">
          <DropdownMenu>
            <DropdownMenuTrigger class="tw:shrink-0 tw:rounded-md tw:p-2 tw:text-zinc-500 tw:hover:bg-zinc-100">
              <Icon name="EllipsisVertical" class="tw:h-5 tw:w-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                variant="destructive"
                :disabled="detaching"
                class="tw:flex tw:items-center tw:gap-2 tw:cursor-pointer"
                @select="detach"
              >
                <Icon name="UserX" class="tw:h-4 tw:w-4" />
                {{ $t('Detach') }}
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="!isValidated"
                class="tw:flex tw:items-center tw:gap-2 tw:cursor-pointer tw:text-primary"
                @select="open"
              >
                <Icon name="Send" class="tw:h-4 tw:w-4" />
                {{ $t('Resend invitation') }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </template>
      </ResendInvitationDialog>
    </div>

    <!-- Brands -->
    <div v-if="isValidated" class="tw:border-t tw:border-zinc-100">
      <template v-if="exponent.brands.length">
        <!-- Column header (once per card) -->
        <div class="tw:grid tw:grid-cols-[minmax(0,1fr)_4.5rem_6rem_5.5rem] tw:items-center tw:gap-x-4 tw:px-4 tw:py-2 tw:bg-zinc-50/70 tw:border-b tw:border-zinc-100">
          <span></span>
          <span class="tw:text-right tw:text-[10px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-zinc-400">{{ $t('Products') }}</span>
          <span class="tw:text-right tw:text-[10px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-zinc-400">{{ $t('Parks linked') }}</span>
          <span class="tw:text-right tw:text-[10px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-zinc-400">{{ $t('Items') }}</span>
        </div>

        <div class="tw:divide-y tw:divide-zinc-100">
          <div
            v-for="brand in exponent.brands"
            :key="brand.id"
            class="tw:px-4 tw:py-2.5 tw:hover:bg-zinc-50"
          >
            <div class="tw:grid tw:grid-cols-[minmax(0,1fr)_4.5rem_6rem_5.5rem] tw:items-center tw:gap-x-4">
              <div class="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
                <span
                  class="tw:h-2 tw:w-2 tw:rounded-full tw:shrink-0"
                  :class="brandIsReady(brand) ? 'tw:bg-green-500' : 'tw:bg-red-500'"
                ></span>
                <a :href="brand.links.show" class="tw:font-medium tw:text-zinc-900 tw:truncate tw:hover:underline">
                  {{ brand.name }}
                </a>
              </div>
              <span class="tw:text-right tw:text-sm tw:tabular-nums tw:text-zinc-700">{{ brand.products_count }}</span>
              <a
                :href="brand.links.parks"
                class="tw:text-right tw:text-sm tw:tabular-nums tw:hover:underline"
                :class="brand.parks_selected_count === 0 ? 'tw:text-red-600' : 'tw:text-zinc-700'"
              >{{ brand.parks_selected_count }} / {{ brand.parks_count }}</a>
              <a
                :href="brand.links.items"
                class="tw:text-right tw:text-sm tw:tabular-nums tw:hover:underline"
                :class="brand.parked_equipments_count === 0 ? 'tw:text-red-600' : 'tw:text-zinc-700'"
              >{{ brand.parked_equipments_count }} / {{ brand.equipments_count }}</a>
            </div>
            <BrandBarcodeBar :barcodes="brand.barcodes" class="tw:mt-2 tw:pl-4" />
          </div>
        </div>
      </template>
      <div v-else class="tw:px-4 tw:py-3 tw:text-sm tw:text-zinc-500">
        {{ $t('No brands for this exhibitor yet.') }}
      </div>
    </div>
    <div v-else class="tw:border-t tw:border-amber-200/70 tw:px-4 tw:py-3 tw:text-sm tw:text-zinc-600">
      {{ $t('Catalog details will appear once the exhibitor accepts the invitation.') }}
    </div>
  </div>
</template>
